import React from 'react';
import { render, hydrate } from 'react-dom';
import { QueryClientProvider } from 'react-query';
import UsersRequireName from 'pages/UsersRequireName';
import { fetchLegacyProps } from './utils/config';
import router from './utils/router';
import BookPage, { bookPageId } from './pages/BookPage';
import MyPage, { myPageId } from './pages/MyPage';
import WelcomePage, { welcomePageId } from './pages/WelcomePage';
import CreateClassifiedPage, { createClassifiedPageId } from './pages/CreateClassifiedPage';
import LoginPage from './pages/LoginPage';
import RegisterUserPage from './pages/RegisterUserPage';
import ChatApp from './ChatApp';
import HeaderPartial from './pages/partials/HeaderPartial';
import { queryClient } from './queryClient';
import { MessageReplyQueueProvider } from './contexts/messageReplyQueueContext';
import ConfirmEmailPage from './pages/ConfirmEmailPage';
var ReactContextWrapper = function (_a) {
    var children = _a.children;
    return React.createElement(MessageReplyQueueProvider, null, children);
};
var QueryClientProviderWrapper = function (_a) {
    var children = _a.children;
    return React.createElement(QueryClientProvider, { client: queryClient }, children);
};
/**
 * react-dom's `render` function give some cryptic error message
 * if it cannot find the DOM element.
 *
 * This function can be called to check if the DOM-element exist,
 * and throws a more fitting error message.
 * @param {string} id The HTML ID
 * @throws {ReferenceError}
 * */
function requireDomElementExists(id) {
    if (document.getElementById(id) == null) {
        throw new ReferenceError("Cannot find React DOM root: " + id);
    }
}
/**
 * As we don't route with react-router
 * and only use react on some modules,
 * we will instead use some manual routing by looking at the
 * URI-segments.
 *
 * NOTE!  This is an intermediate solution to help mitigate the technical debt
 *        from our legacy JavaScript,
 *        before we fully separate the front-end and back-end.
 *
 *        The legacy JS uses an older version of JQuery.
 *        There is also no form of tests or use of ES2015+ syntax.
 *        As a consequence working with front-end JavaScript is a pain
 *
 * @param {string} uri The page's URI
 * @throws {ReferenceError} Thrown if the function cannot find the React DOM roots
 * */
// eslint-disable-next-line import/prefer-default-export
export function loadComponents(uri) {
    var config = fetchLegacyProps(); // Fetch the IBOK properties from iBok
    var userId = config.user ? config.user.id : null;
    // Reuse our server-side-rendered header
    hydrate(React.createElement(QueryClientProviderWrapper, null,
        React.createElement(ReactContextWrapper, null,
            React.createElement(HeaderPartial, null))), document.getElementById('globalHeader'));
    var routes = [
        {
            matcher: function (segments) { return segments[0] === ''; },
            handler: function () {
                requireDomElementExists(welcomePageId);
                render(React.createElement(WelcomePage, null), document.getElementById(welcomePageId));
            },
        },
        {
            matcher: function (segments) { return segments[0] === 'bok'; },
            handler: function (segments) {
                requireDomElementExists(bookPageId);
                var isbn = segments[1];
                render(React.createElement(QueryClientProviderWrapper, null,
                    React.createElement(ReactContextWrapper, null,
                        React.createElement(BookPage, { isbn: isbn, userId: userId }))), document.getElementById(bookPageId));
            },
        },
        {
            matcher: function (segments) { return segments[0] === 'sett_inn_annonse'; },
            handler: function () {
                requireDomElementExists(createClassifiedPageId);
                render(React.createElement(QueryClientProviderWrapper, null,
                    React.createElement(CreateClassifiedPage, null)), document.getElementById(createClassifiedPageId));
            },
        },
        {
            matcher: function (segments) { return segments[0] === 'min_side'; },
            handler: function () {
                requireDomElementExists(myPageId);
                render(React.createElement(MyPage, null), document.getElementById(myPageId));
            },
        },
        {
            matcher: function (segments) { return segments[0] === 'registrer_navn'; },
            handler: function () {
                render(React.createElement(QueryClientProviderWrapper, null,
                    React.createElement(UsersRequireName, null)), document.getElementById('registerName'));
            },
        },
        {
            matcher: function (segments) { return segments[0] === 'logg_inn'; },
            handler: function () {
                render(React.createElement(QueryClientProviderWrapper, null,
                    React.createElement(LoginPage, null)), document.getElementById('loginapp'));
            },
        },
        {
            matcher: function (segments) { return segments[0] === 'registrer'; },
            handler: function () {
                // Register page will also render the success view
                // which does not include element 'registeruserapp'
                if (!document.getElementById('registeruserapp')) {
                    return;
                }
                render(React.createElement(QueryClientProviderWrapper, null,
                    React.createElement(RegisterUserPage, null)), document.getElementById('registeruserapp'));
            },
        },
        {
            matcher: function (segments) { return segments[0] === 'chat'; },
            handler: function () {
                render(React.createElement(QueryClientProviderWrapper, null,
                    React.createElement(ReactContextWrapper, null,
                        React.createElement(ChatApp, null))), document.getElementById('chatapp'));
            },
        },
        {
            matcher: function (segments) { return segments[0] === 'bekreft_epost'; },
            handler: function () {
                render(React.createElement(QueryClientProviderWrapper, null,
                    React.createElement(ReactContextWrapper, null,
                        React.createElement(ConfirmEmailPage, null))), document.getElementById('confirmEmail'));
            },
        },
    ];
    router(routes, uri);
}
